import { CSSProperties, ReactElement, useContext, useEffect, useMemo, useRef, useState } from "react";
import atlas, { AuthenticationType, HtmlMarker, HtmlMarkerOptions, SymbolLayerOptions, data, layer, source } from 'azure-maps-control'
import { AzureMap, AzureMapsProvider, IAzureMapsContextProps, AzureMapsContext, IAzureMapOptions, AzureMapDataSourceProvider, AzureMapLayerProvider, IAzureDataSourceChildren, AzureMapFeature, AzureMapHtmlMarker, IAzureMapHtmlMarkerEvent, IAzureMapFeature, IAzureMapHtmlMarker } from 'react-azure-maps'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapPin } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import 'azure-maps-control/dist/atlas.min.css'
import './styles/MapControl.scss';
import { Spinner } from "react-bootstrap";
import { color } from "html2canvas/dist/types/css/types/color";

/*
https://github.com/Azure/react-azure-maps
https://azure.github.io/react-azure-maps/index.html
https://github.com/Azure-Samples/AzureMapsCodeSamples/tree/main/Samples
https://github.com/Azure-Samples/AzureMapsCodeSamples/blob/main/Samples/HTML%20Markers/Draggable%20HTML%20Marker/Draggable%20HTML%20Marker.html
https://github.com/Azure-Samples/AzureMapsCodeSamples/blob/main/Samples/Polygons/Geospatially%20Accurate%20Circle/Geospatially%20Accurate%20Circle.html
https://github.com/Azure/react-azure-maps-playground/blob/master/src/examples/AzureLayer.tsx
https://github.com/Azure-Samples/AzureMapsCodeSamples/blob/main/Samples/Heat%20Map%20Layer/Cluster%20weighted%20Heat%20Map/Cluster%20weighted%20Heat%20Map.html
https://azure.github.io/react-azure-maps/globals.html#position
*/

const defaultOptions: IAzureMapOptions = {
    style: undefined,
    authOptions: {
        authType: AuthenticationType.subscriptionKey,
        subscriptionKey: "XiJNGIfMyRafhsXaJN3RdXDh8Nh6s2upuLY5gs-BHA4" // todo: get from global state / user info call!
    },
};

const dataSourceRef = new source.DataSource();
const layerRef = new layer.SymbolLayer(dataSourceRef);
const polygonDataSourceRef = new source.DataSource();
const polygonLayerRef = new layer.PolygonLayer(
    polygonDataSourceRef,
    "polygonLayer",
    {
        fillColor: ["get", "color"],
        fillOpacity: ["get", "opacity"],
    });

export interface IAzureMapMasterComponentDefaultPosition {
    latitude: number;
    longitude: number;
}

export interface IAzureMapMasterComponentPin {
    latitude: number;
    longitude: number;
    title?: string;
    icon?: "marker-black" | "marker-blue" | "marker-darkblue" | "marker-red" | "marker-yellow" | "pin-blue" | "pin-darkblue" | "pin-red" | "pin-round-blue" | "pin-round-darkblue" | "pin-round-red"
}

export interface IAzureMapMasterComponentDraggablePin {
    latitude: number;
    longitude: number;
    radius?: number;
    color?: string;
    opacity?: number;
}

export interface IAzureMapMasterComponentHtmlMarkerOptions {
    latitude: number;
    longitude: number;
    text: string;
    title: string;
    content?: ReactElement;
    style?: CSSProperties;
}

export interface IAzureMapMasterComponentPinStyles {
    name: string;
    pinIcon?: IconProp;
    pinWidth?: string;
    pinHeight?: string;
    pinStyle?: CSSProperties;
}

interface IMapControlProps {
    options?: IAzureMapOptions;
    mapClickCallback?: (latitude: number, longitude: number) => void;
    mapZoomCallback?: (zoom: number) => void;
    mapDragCallback?: (latitude: number, longitude: number) => void;
    pinClickCallback?: (marker: any) => void;
    htmlMarkerClickCallback?: (marker: any) => void;
    pinClusterClickedCallback?: (e: any) => void;
    draggablePin?: IAzureMapMasterComponentDraggablePin;
    circles?: IAzureMapMasterComponentDraggablePin[];
    position?: IAzureMapMasterComponentDefaultPosition;
    zoom?: number;
    htmlMarkers?: IAzureMapMasterComponentHtmlMarkerOptions[];
    mapWidth?: string;
    mapHeight?: string;
    onDragCallback?: (latitude: number, longitude: number) => void;
    useHeatMap?: boolean;
    useClustering?: boolean;
    clusterRadius?: number;
    clusterMaxZoom?: number;
    clusterMinPoints?: number;
    heatMapRadius?: number;
    heatMapWeight?: any;
    defaultPinWidth?: string;
    defaultPinHeight?: string;
    defaultPinStyle?: CSSProperties;
    pins?: IAzureMapMasterComponentPin[];
    pinStyles?: IAzureMapMasterComponentPinStyles[];
    heatMapOpacity?: number;
    loadingLocations?: boolean;
}

interface IAzureMapMasterComponentProps {
    options: IAzureMapOptions;
    clickHandler?: (e: any) => void;
    zoomHandler?: (e: any) => void;
    dragHandler?: (e: any) => void;
    pinClickCallback?: (marker: any) => void;
    htmlMarkerClickCallback?: (marker: any) => void;
    pinClusterClickedCallback?: (e: any) => void;
    pinClusterDoubleClickedCallback?: (e: any) => void;
    pins?: IAzureMapMasterComponentPin[];
    htmlMarkers?: IAzureMapMasterComponentHtmlMarkerOptions[];
    mapWidth?: string;
    mapHeight?: string;
    useHeatMap?: boolean;
    useClustering?: boolean;
    clusterRadius?: number;
    clusterMaxZoom?: number;
    clusterMinPoints?: number;
    heatMapRadius?: number;
    heatMapWeight?: any;
    pinIcon?: IconProp;
    pinSize?: string;
    heatMapOpacity?: number;
}

const MapControl = (props: IMapControlProps) => {
    return (
        <AzureMapsProvider>
            <div className={`mapContainer ${props.loadingLocations ? "loading" : ""}`}>
                <div className="mapLoader">
                    <Spinner animation="border" />
                </div>
                <MapControlInner {...props} />
            </div>
        </AzureMapsProvider>
    )
}

const MapControlInner = (props: IMapControlProps) => {
    const { mapRef, isMapReady } = useContext<IAzureMapsContextProps>(AzureMapsContext);
    const [circle, setCircle] = useState<any>();
    const circleRef = useRef(circle);
    circleRef.current = circle;
    const draggableMarkerRef = useRef<HtmlMarker>();
    const [lastPosition, setLastPosition] = useState<{ latitude: number, longitude: number }>();

    useEffect(() => {
        if (isMapReady && mapRef) {
            console.log("Setting up");
            mapRef.sources.add(polygonDataSourceRef);
            mapRef.sources.add(dataSourceRef);
            mapRef.layers.add(polygonLayerRef);
            mapRef.layers.add(layerRef);
            mapRef.setStyle({ showTileBoundaries: false });
        }
    }, [isMapReady, mapRef]);

    useEffect(() => {
        if (isMapReady && mapRef && props.position && (props.position?.latitude != lastPosition?.latitude && props.position?.longitude != lastPosition?.longitude)) {
            setLastPosition(props.position);
            console.log("Changing position to " + props.position?.latitude + ", " + props.position?.longitude);
            mapRef.setCamera({
                center: [
                    props?.position?.longitude ?? 0,
                    props?.position?.latitude ?? 0]
            });
        }
    }, [mapRef, isMapReady, props.position]);

    useEffect(() => {
        if (isMapReady && mapRef) {
            console.log("Changing zoom to " + props.zoom);
            mapRef.setCamera({
                zoom: props.zoom ?? 4
            });
        }
    }, [mapRef, isMapReady, props.zoom])

    useEffect(() => {
        if (mapRef && polygonDataSourceRef) {
            mapRef.markers.clear();
            polygonDataSourceRef.clear();

            if (draggableMarkerRef.current) {
                mapRef?.events.remove("dragend", draggableMarkerDragEndHandler);
            }

            setTimeout(() => {
                if (props.draggablePin) {
                    addDraggableMarkerToMap(props.draggablePin.longitude, props.draggablePin.latitude, props.draggablePin.radius);
                }
            }, 100);
        }
    },
        [isMapReady, mapRef, props.draggablePin]);

    useEffect(() => {
        updatePolygons();
    }, [props.circles]);

    const updatePolygons = () => {
        if (mapRef && polygonLayerRef) {
            polygonDataSourceRef.clear();

            if (circleRef.current) {
                polygonDataSourceRef.add(circleRef.current);
            }

            props.circles?.forEach(circle => {
                if (circle.radius) {
                    var shape =
                        getCircle(
                            circle.longitude,
                            circle.latitude,
                            circle.radius,
                            circle.color,
                            circle.opacity
                        );

                    polygonDataSourceRef.add(shape);
                }
            });
        }
    };

    const clickHandler = (e: any) => {
        const pos = e.position;
        if (props.mapClickCallback && pos && pos.length === 2) {
            props.mapClickCallback(pos[1], pos[0]);
        }
    };

    const zoomHandler = (e: any) => {
        const zoomLevel = e.map.map.getZoom();
        if (props.mapZoomCallback && zoomLevel) {
            props.mapZoomCallback(zoomLevel);
        }
    };

    const dragHandler = (e: any) => {
        /* todo: where do we get the new center position?
        const pos = e.position;
        if (props.mapDragCallback && pos && pos.length === 2) {
            props.mapDragCallback(pos[1], pos[0]);
        }
        */
    };

    const addDraggableMarkerToMap = (longitude: number, latitude: number, radius?: number, styleName?: string, customHtml?: HTMLElement) => {
        if (mapRef) {
            var marker = getDraggableMarker(longitude, latitude, styleName, customHtml);

            if (marker) {
                draggableMarkerRef.current = marker;

                if (radius) {
                    var circle =
                        getCircle(
                            longitude,
                            latitude,
                            radius,
                            "#00AAFF",
                            0.2);

                    circleRef.current = circle;
                    setCircle(circle);
                    updatePolygons();
                }

                mapRef.events.add(
                    "dragend",
                    marker,
                    draggableMarkerDragEndHandler);

                mapRef.markers.add(marker);
            }
        }
    };

    const draggableMarkerDragEndHandler = (e: any) => {
        if (draggableMarkerRef.current) {
            const options = draggableMarkerRef.current.getOptions();
            const pos = options.position;

            if (circleRef.current) {
                console.log(circleRef.current);
                circleRef.current.setCoordinates(pos);
            }

            if (pos && props.onDragCallback) {
                props.onDragCallback(Math.round(pos[1] * 100000) / 100000, Math.round(pos[0] * 100000) / 100000);
            }
        }
    };

    const getDraggableMarker = (longitude: number, latitude: number, styleName?: string, customHtml?: HTMLElement) => {
        var icon = document.getElementById(styleName || 'defaultPinStyle')?.cloneNode(true) as HTMLElement;
        if (icon) {
            icon.style.display = 'block';
            return (
                new atlas.HtmlMarker({
                    draggable: true,
                    htmlContent: customHtml || icon,
                    position: [longitude, latitude],
                    pixelOffset: [0, 5]
                })
            );
        }
    };

    const getCircle = (longitude: number, latitude: number, radius: number, color?: string, opacity?: number) => {
        return (
            new atlas.Shape(
                new atlas.data.Point([longitude, latitude]),
                "circle",
                {
                    color: color || "#00AAFF",
                    opacity: opacity || 1,
                    subType: "Circle",
                    radius: milesToMeters(radius) // in meters normally!
                }));
    }

    const milesToMeters = (miles: number) => {
        return miles * 1609.34;
    };

    const pinClusterDoubleClicked = (e: any) => {
        var cluster = e;

        if (cluster && mapRef) {
            dataSourceRef.getClusterExpansionZoom(cluster.properties.cluster_id)
                .then((zoom) => {
                    mapRef.setCamera({
                        center: cluster.getOptions().position,
                        zoom: zoom
                    });
                });
        }
    };

    return (<>
        <div // offset 0,5
            id="defaultPinStyle"
            style={{
                display: "none",
                width: props.defaultPinWidth || "30px",
                height: props.defaultPinHeight || "30px"
            }} >
            <FontAwesomeIcon
                icon={faMapPin as IconProp}
                style={{
                    ...{
                        pointerEvents: "none",
                        width: props.defaultPinWidth || "30px",
                        height: props.defaultPinHeight || "30px"
                    }, ...props.defaultPinStyle
                }} />
        </div>

        {
            props.pinStyles
                ?.map(style =>
                    <div
                        id={style.name}
                        style={{
                            display: "none",
                            width: style.pinWidth || "30px",
                            height: style.pinHeight || "30px"
                        }} >
                        <FontAwesomeIcon
                            icon={(style.pinIcon || faMapPin) as IconProp}
                            style={{
                                ...{
                                    pointerEvents: "none",
                                    width: style.pinWidth || "30px",
                                    height: style.pinHeight || "30px"
                                }, ...style.pinStyle
                            }} />
                    </div>
                )
        }

        <AzureMapMasterComponent
            options={{ ...defaultOptions, ...props.options }}
            clickHandler={clickHandler}
            zoomHandler={zoomHandler}
            dragHandler={dragHandler}
            pinClickCallback={props.pinClickCallback}
            htmlMarkerClickCallback={props.htmlMarkerClickCallback}
            pinClusterClickedCallback={props.pinClusterClickedCallback}
            pinClusterDoubleClickedCallback={pinClusterDoubleClicked}
            pins={props.pins}
            htmlMarkers={props.htmlMarkers}
            mapHeight={props.mapHeight}
            mapWidth={props.mapWidth}
            useHeatMap={props.useHeatMap}
            useClustering={props.useClustering}
            clusterRadius={props.clusterRadius}
            clusterMaxZoom={props.clusterMaxZoom}
            clusterMinPoints={props.clusterMinPoints}
            heatMapRadius={props.heatMapRadius}
            heatMapWeight={props.heatMapWeight}
            heatMapOpacity={props.heatMapOpacity}
        />
    </>
    )
}

const AzureMapMasterComponent = (props: IAzureMapMasterComponentProps) => {
    const renderPin = (pin: IAzureMapMasterComponentPin): IAzureMapFeature => {
        const rendId = Math.random();

        return (
            <AzureMapFeature
                key={rendId}
                id={rendId.toString()}
                type="Point"
                coordinate={[pin.longitude, pin.latitude]}
                properties={{
                    title: pin.title || "",
                    icon: pin.icon || 'pin-round-blue',
                }}
            />
        );
    };

    const eventToMarker: Array<IAzureMapHtmlMarkerEvent> = [{ eventName: 'click', callback: props.htmlMarkerClickCallback || (() => { }) }];

    const renderHtmlMarker = (marker: IAzureMapMasterComponentHtmlMarkerOptions): any => {
        const rendId = Math.random();
        return (
            <AzureMapHtmlMarker
                key={rendId}
                markerContent={
                    <div
                        style={{
                            ...{
                                backgroundColor: "rgba(255,255,255, 0.5)",
                                width: "160px",
                                height: "40px",
                                borderRadius: "7px",
                                padding: "4px",
                                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
                            },
                            ...marker.style
                        }}>
                        {marker.content || <p>{marker.text}</p>}
                    </div>
                }
                options={{
                    anchor: "bottom-left",
                    position: [
                        marker.longitude,
                        marker.latitude
                    ],
                    text: marker.text,
                    title: marker.title,
                }}
                events={eventToMarker}
            />
        );
    }

    const memoizedPinRender: IAzureDataSourceChildren = useMemo(
        (): any => props.pins?.map(pin => renderPin(pin)),
        [props.pins]);

    const memoizedHtmlMarkerRender: IAzureDataSourceChildren = useMemo(
        (): any => props.htmlMarkers?.map(marker => renderHtmlMarker(marker)),
        [props.htmlMarkers]);

    const clusterClicked = (e: any) => {
        if (props.pinClusterClickedCallback) {
            props.pinClusterClickedCallback(e);
        }
    }

    const clusterDoubleClick = (e: any) => {
        console.log("DOUBLE CLICKED CLUSTER?", e);
        if (props.pinClusterDoubleClickedCallback) {
            props.pinClusterDoubleClickedCallback(e);
        }
    };

    return (
        <div style={{ height: props.mapHeight || '500px', width: props.mapWidth || "100%" }}>
            <AzureMap
                options={props.options}
                events={{
                    click: props.clickHandler,
                    zoomend: props.zoomHandler,
                    dragend: props.dragHandler
                }}>
                <AzureMapDataSourceProvider
                    id={'AzureMapDataSourceProvider'}
                    options={{
                        cluster: props.useClustering || false,
                        clusterRadius: props.clusterRadius || 10,
                        clusterMinPoints: props.clusterMinPoints || 2,
                        clusterMaxZoom: props.clusterMaxZoom || 6,
                        allowOverlap: true,
                        ignorePlacement: true
                    }}>
                    {
                        props.useHeatMap ?
                            <AzureMapLayerProvider
                                type={"HeatLayer"}
                                options={{
                                    weight: props.heatMapWeight || ["get", "point_count"],
                                    radius: props.heatMapRadius || 20,
                                    opacity: props.heatMapOpacity || 0.8
                                }} />
                            : null
                    }
                    <AzureMapLayerProvider
                        id={'AzureMapLayerProvider'}
                        options={{
                            textOptions: {
                                color: "black",
                                size: 16,
                            },
                            allowOverlap: true,
                            ignorePlacement: true
                        }}
                        events={{
                            click: clusterClicked,
                            dbclick: clusterDoubleClick
                        }}
                        lifecycleEvents={{
                            layeradded: () => { },
                        }}
                        type={"SymbolLayer"}
                    />
                    <AzureMapLayerProvider
                        id={'AzureMapLayerProvider2'}
                        type={"PolygonLayer"}
                        options={{
                            fillColor: ["get", "color"],
                            fillOpacity: ["get", "opacity"],
                            strokeColor: ['get', 'color'],
                            strokeWidth: 3
                        }}
                    />
                    {props.pins ? memoizedPinRender : null}
                    {props.htmlMarkers ? memoizedHtmlMarkerRender : null}
                </AzureMapDataSourceProvider>
            </AzureMap>
        </div>
    )
}

export default MapControl;